/* eslint-disable react-hooks/exhaustive-deps */
import {
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type TableOptions,
} from '@tanstack/react-table';
import { useMemo } from 'react';

import generateColumnsWithDefaultColumns from '@design-system/molecules/ticlick-table/default-columns';
import { useInitializeTableStates } from '@src/design-system/molecules/ticlick-table/hooks/use-initialize-table-states';
import { PersistentRowSelectionFeature } from '@src/design-system/molecules/ticlick-table/features/persistent-row-selection';

import { convertActiveFiltersToApiParams } from '@shared/features/views/helpers/active-filters-to-api-params';
import type {
  FetchFunction,
  UseTiclickTableConfigProps,
} from '@src/design-system/molecules/ticlick-table/types';
import { keepPreviousData } from '@tanstack/react-query';
import { getIsColumnOrderChanged } from '../utils/helpers/get-is-column-order-changed';
import type { VisibilityState } from '@tanstack/react-table';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const defaultTableConfig: TableOptions<any> = {
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  getFilteredRowModel: getFilteredRowModel(),
  getFacetedUniqueValues: getFacetedUniqueValues(),
  enableSortingRemoval: false,
  enableRowSelection: true,
  manualFiltering: true,
  manualPagination: true,
  enableColumnResizing: true,
  columnResizeMode: 'onChange',
  getRowId: (row) => row?.id,
  _features: [PersistentRowSelectionFeature],

  columns: [],
  data: [],
};

const useTiclickTableConfig = <T, F extends FetchFunction<T>>({
  options,
  rowActions,
  identifyingData,
  fetchingData,
  filters: filtersComponent,
  initialSettings,
}: UseTiclickTableConfigProps<T, F>) => {
  const { viewEntityType, viewEntityId } = identifyingData;
  const { query, options: queryOptions, fetchFn } = fetchingData || {};

  const {
    columnOrder,
    onColumnOrderChange,

    columnPinning,
    onColumnPinningChange,
    isColumnPinningChanged,

    columnVisibility,
    onColumnVisibilityChange,
    isColumnVisibilityChanged,

    columnNames,
    setColumnNames,
    isNamesChanged,

    columnSizing,
    onColumnSizingChange,

    sorting,
    onSortingChange,

    pagination,
    onPaginationChange,

    tableFilters,
    onGlobalFilterChange,

    currentView,
    setCurrentView,
    isViewFetched,
  } = useInitializeTableStates(identifyingData, initialSettings);

  const isAllowedToFetchTableData =
    (!viewEntityType && !viewEntityId) || isViewFetched;
  const queryOptions_ = useMemo(
    () => ({
      ...queryOptions,
      enabled: isAllowedToFetchTableData && (queryOptions?.enabled ?? true),
      placeholderData: keepPreviousData,
    }),
    [queryOptions, isAllowedToFetchTableData]
  );

  const filters = convertActiveFiltersToApiParams(tableFilters?.staticFilters);

  // A table that sorts on the server (`manualSorting`) sends its sort as a
  // DRF-style `ordering` — the column id, `-`-prefixed when descending. The
  // fetch function maps column ids onto the API's field names. Tables that
  // sort their loaded page in the browser send nothing, as before.
  const ordering =
    options.manualSorting && sorting[0]
      ? `${sorting[0].desc ? '-' : ''}${sorting[0].id}`
      : undefined;

  const queryData = fetchFn?.(
    {
      ...(typeof query === 'function'
        ? (query as (tableFilters: Parameters<F>[0]) => Parameters<F>[0])(
            tableFilters
          )
        : (query ?? ({} as Parameters<F>[0]))),
      ...filters,
      view: currentView?.id,
      page_size: pagination.pageSize,
      page_number: pagination.pageIndex + 1,
      ...(ordering ? { ordering } : {}),
    },
    queryOptions_
  );
  const queryResults = queryData?.data?.results || [];

  const columns = useMemo(() => {
    return typeof options.columns === 'function'
      ? options.columns(queryResults)
      : options.columns || [];
  }, [options.columns, queryResults]);

  const data = useMemo(() => {
    return typeof options.data === 'function'
      ? options.data(queryResults)
      : queryResults;
  }, [JSON.stringify(options.data), JSON.stringify(queryResults)]);

  const selectable = options.enableRowSelection !== false;
  const finalColumns = useMemo(
    () =>
      generateColumnsWithDefaultColumns(columns, rowActions, { selectable }),
    [columns, JSON.stringify(rowActions), selectable]
  );

  const tableMetrics = useMemo(
    () => ({
      rowCount: queryData?.data?.count || 10,
      pageCount: queryData?.data?.total_pages || 1,
    }),
    [queryData?.data?.count, queryData?.data?.total_pages]
  );

  const defaultColumnOrder = initialSettings?.ordering?.length
    ? initialSettings.ordering
    : [...columns.map((c) => c.id ?? ''), 'actions'];

  const hasSavedViewVisibility = Boolean(
    Object.keys(currentView?.visible_static_fields?.visibility || {}).length
  );

  // Apply isInitialVisible:false for dynamic columns (e.g. custom fields) when
  // there is no saved view visibility — without writing into dirty state.
  const resolvedColumnVisibility = useMemo(() => {
    if (hasSavedViewVisibility) return columnVisibility;

    const merged: VisibilityState = { ...(columnVisibility || {}) };
    for (const column of finalColumns) {
      if (
        column.meta?.isInitialVisible === false &&
        column.id &&
        merged[column.id] === undefined
      ) {
        merged[column.id] = false;
      }
    }
    return merged;
  }, [columnVisibility, finalColumns, hasSavedViewVisibility]);

  // === TABLE CONFIGURATION ===
  const table = useReactTable({
    ...defaultTableConfig,

    ...tableMetrics,
    ...options,

    data,
    columns: finalColumns,

    onColumnVisibilityChange,
    onColumnSizingChange,
    onColumnPinningChange,
    onColumnOrderChange,
    onPaginationChange,
    onSortingChange,
    onGlobalFilterChange,

    meta: {
      maxPinnedColumns: 3,

      getAllColumnPinnedCount(): number {
        const { left = [], right = [] } = columnPinning;
        return left.length + right.length;
      },

      getLeafColumnPinnedCount(): number {
        const { left = [], right = [] } = columnPinning;
        return [...left, ...right].filter(
          (p: string) => p !== 'select' && p !== 'actions'
        ).length;
      },

      isColumnPinningChanged,
      isColumnVisibilityChanged,
      isNamesChanged,
      isColumnOrderChanged: getIsColumnOrderChanged({
        currentColumnOrder: columnOrder,
        currentView,
        initialColumnOrder: defaultColumnOrder,
      }),

      state: {
        columnNames,
        setColumnNames,

        currentView,
        setCurrentView,
      },

      identifyingData,
      filters: filtersComponent,
      initialSettings,
      queryData,

      ...options.meta,
    },

    state: useMemo(
      () => ({
        pagination,
        columnVisibility: resolvedColumnVisibility,
        columnPinning,
        columnSizing,
        sorting,
        columnOrder,
        globalFilter: tableFilters,
        ...options.state,
      }),
      [
        pagination,
        resolvedColumnVisibility,
        columnPinning,
        columnSizing,
        columnOrder,
        sorting,
        tableFilters,
        options.state,
      ]
    ),
  });

  return table;
};

export default useTiclickTableConfig;
